<template>
  <div class="go-overlay">
    <div class="go-page" :class="{'go-page-selection': selectionMode}">
      <!-- ── LOADING PHASE ── -->
      <template v-if="status != 2">
        <div class="go-loading-wrap">
          <div class="go-loader-icon">
            <i class="fa fa-spinner fa-spin"></i>
          </div>
          <h2 class="go-loading-text">CALCULATING THE SCORE...</h2>
          <p class="go-loading-sub">Wait for a moment while we finalize the results</p>
        </div>
      </template>

      <!-- ── SELECTION PHASE ── -->
      <template v-else-if="selectionMode">
        <div class="go-sel-header">
          <div class="go-header-pill">
            <i class="fa fa-gamepad"></i> GAME OVER
            <span class="go-header-sep">|</span>
            <span class="go-header-sub">SELECT WINNING TEAM</span>
          </div>
        </div>

        <h1 class="go-sel-main-title">SELECT THE WINNING TEAMS</h1>

        <div class="go-sel-grid">
          <div
            v-for="team in sortedTeams"
            :key="team.id"
            class="go-sel-card"
            :style="{
              borderLeftColor: team.color,
              '--team-glow': team.color + '40',
              '--team-color': team.color,
            }">
            <div class="go-sel-card-header">
              <div class="go-tooltip-wrapper">
                <div class="go-sel-team-name" :style="{color: team.color}">{{ truncateLetters(team.name, 4) }}</div>
                <div class="go-custom-tooltip" :style="{borderColor: team.color}">{{ team.name }}</div>
              </div>
              <div class="go-sel-badges">
                <div class="go-sel-player-count" :style="{color: team.color, borderColor: team.color + '40'}">{{ (team.score || 0).toLocaleString() }} PTS</div>
                <div class="go-sel-player-count" :style="{color: team.color, borderColor: team.color + '40'}">
                  {{ team.members ? team.members.length : 0 }} PLAYERS
                </div>
              </div>
            </div>

            <div class="go-sel-members">
              <div v-for="(mem, idx) in team.members" :key="idx" class="go-sel-member">
                <div class="go-sel-av" :style="{background: avatarBg(idx)}">
                  <img v-if="mem.avatar" :src="mem.avatar" style="width: 100%; height: 100%; border-radius: 50%; object-fit: cover" />
                  <span v-else>{{ getInitials(mem.name) }}</span>
                </div>
                <div class="go-sel-name">{{ mem.name }}</div>
              </div>
            </div>

            <button class="go-declare-btn" :style="{backgroundColor: team.color, color: getContrastColor(team.color)}" @click="declareWinner(team)">
              DECLARE WINNER
            </button>
          </div>
        </div>
      </template>

      <!-- ── RESULTS PHASE ── -->
      <template v-else>
        <!-- HEADER PILL -->
        <div class="go-header-pill"><i class="fa fa-gamepad" style="margin-right: 6px"></i> GAME OVER</div>

        <!-- ── WINNER SECTION ── -->
        <div class="go-winner-wrap">
          <!-- Left: Trophy column -->
          <div class="go-trophy-col">
            <span class="go-sparkle go-sp-tl">☆</span>
            <span class="go-sparkle go-sp-tr">✦</span>
            <span class="go-sparkle go-sp-tr2">✦</span>
            <span class="go-confetti">🎉</span>

            <div class="go-trophy-glow-wrap">
              <i class="fa fa-trophy go-trophy-icon"></i>
            </div>

            <div class="go-winner-badge">WINNER</div>
          </div>

          <!-- Right: Winner info -->
          <div class="go-winner-info" v-if="winner">
            <div class="go-winner-name" :style="{color: winner.color || '#ff00ff'}">
              {{ winner.name }}
            </div>
            <div class="go-pts-pill">
              <i class="fa fa-bolt" style="font-size: 11px; margin-right: 4px"></i>
              {{ (winner.score || 0).toLocaleString() }} PTS
            </div>

            <!-- Members card -->
            <div class="go-members-card" v-if="winner.members && winner.members.length">
              <div v-for="(mem, idx) in winner.members.slice(0, 5)" :key="idx" class="go-mem-col">
                <div class="go-mem-avatar" :style="{background: avatarBg(idx)}">
                  <img v-if="mem.avatar" :src="mem.avatar" style="width: 100%; height: 100%; border-radius: 50%; object-fit: cover" />
                  <span v-else>{{ getInitials(mem.name) }}</span>
                </div>
                <div class="go-mem-name">{{ mem.name.split(' ')[0].toUpperCase() }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── DIVIDER ── -->
        <div class="go-divider"></div>

        <!-- ── MOBILE TABS ── -->
        <div class="go-mobile-tabs">
          <div class="go-tab" :class="{active: mobileActiveTab === 'teams'}" @click="mobileActiveTab = 'teams'">All Team Scores</div>
          <div class="go-tab" :class="{active: mobileActiveTab === 'perf'}" @click="mobileActiveTab = 'perf'">Class Performance</div>
        </div>

        <!-- ── LEADERBOARD GRID ── -->
        <div class="go-lb-grid" :class="{'go-mobile-hidden': mobileActiveTab !== 'teams'}">
          <div v-for="(team, i) in otherTeams" :key="team.id" class="go-lb-card" :style="{borderLeftColor: team.color || '#00ffff'}">
            <div class="go-lb-body">
              <div class="go-lb-name">{{ team.name }}</div>
              <div class="go-lb-avatars">
                <div v-for="(mem, mi) in (team.members || []).slice(0, 3)" :key="mi" class="go-mini-av" :style="{background: avatarBg(mi)}">
                  <img v-if="mem.avatar" :src="mem.avatar" style="width: 100%; height: 100%; border-radius: 50%; object-fit: cover" />
                  <span v-else>{{ getInitials(mem.name) }}</span>
                </div>
                <div v-if="team.members && team.members.length > 3" class="go-mini-av go-mini-more">+{{ team.members.length - 3 }}</div>
              </div>
            </div>
            <div class="go-lb-score-col">
              <div class="go-lb-pts" :style="{color: team.color || '#ffaa00'}">
                {{ (team.score || 0).toLocaleString() }}
              </div>
              <div class="go-lb-pts-lbl">POINTS</div>
            </div>
          </div>
        </div>

        <!-- ── CLASS PERFORMANCE ── -->
        <div v-if="analysis" class="go-perf-wrap" :class="{'go-mobile-hidden': mobileActiveTab !== 'perf'}">
          <div class="go-perf-title"><i class="fa fa-bar-chart" style="margin-right: 6px"></i> CLASS PERFORMANCE</div>
          <div class="go-perf-row">
            <div class="go-acc-block">
              <div class="go-acc-pct" :style="{color: accuracyColor}">{{ overallAccuracy }}%</div>
              <div class="go-acc-lbl">OVERALL ACCURACY</div>
            </div>
            <div class="go-perf-stats-wrap">
              <div class="go-stat-complex">
                <i class="fa fa-check-circle" style="color: #00ff88; font-size: 22px"></i>
                <div class="go-stat-block">
                  <div class="go-stat-num" style="color: #00ff88">{{ correctCount }}</div>
                  <div class="go-stat-lbl">CORRECT</div>
                </div>
              </div>
              <div class="go-x-icon">✕</div>
              <div class="go-stat-block">
                <div class="go-stat-num" style="color: #ff3366">{{ incorrectCount }}</div>
                <div class="go-stat-lbl" style="color: #ff3366">INCORRECT</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── RETURN BUTTON ── -->
        <button class="go-return-btn" @click="onReturn"><i class="fa fa-sign-out" style="margin-right: 8px"></i> RETURN TO CLASS</button>

        <!-- ── COUNTDOWN ── -->
        <div class="go-countdown-wrap">
          <div class="go-cd-bar-wrap">
            <div class="go-cd-bar" :style="{width: countdownPct + '%'}"></div>
          </div>
          <span class="go-cd-text">Returning to class in {{ countdown }}s...</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
const AV_COLORS = ['#0d3535', '#1a2a35', '#1a1a35', '#35200d', '#200d35']
const LVL_COLORS = ['#ff3366', '#ff3366', '#00ff88', '#ff3366']

export default {
  name: 'GameOverModal',
  props: {
    teams: {type: Array, default: () => []},
    analysis: {type: Object, default: () => null},
    slides: {type: Array, default: () => []},
    sessionQuestions: {type: Array, default: () => []},
    winnerId: {type: [String, Number], default: null},
    status: {type: Number, default: 1},
  },
  data() {
    return {
      countdown: 10,
      countdownInterval: null,
      mobileActiveTab: 'teams',
      selectionMode: true,
      selectedWinner: null,
    }
  },
  computed: {
    sortedTeams() {
      return [...this.teams].sort((a, b) => (b.score || 0) - (a.score || 0))
    },
    winner() {
      return this.selectedWinner || this.sortedTeams[0] || null
    },
    winningTeamFromRemote() {
      if (!this.winnerId) return null
      return this.teams.find((t) => t.id == this.winnerId)
    },
    otherTeams() {
      const w = this.winner
      if (!w) return this.sortedTeams
      return this.sortedTeams.filter((t) => t.id !== w.id)
    },
    correctCount() {
      if (this.analysis && this.analysis.slides) {
        console.log("endGame in over modal analysis", this.analysis)
        return this.analysis.slides.reduce((sum, s, i) => {
          let isValid = false
          if (s.optionsStats && Array.isArray(s.optionsStats) && s.optionsStats.some(opt => opt.on || opt.isCorrect)) {
            isValid = true
          } else {
            const pageId = s.pageId || s.page || s.id || (this.slides && this.slides.length === this.analysis.slides.length ? (this.slides[i] && (this.slides[i].page || this.slides[i].id)) : null)
            isValid = this.hasValidAnswer(pageId)
          }
          if (isValid) {
            return sum + (s.correctResponses || 0)
          }
          return sum
        }, 0)
      }
      return 0
    },
    incorrectCount() {
      if (this.analysis && this.analysis.slides) {
        const total = this.analysis.slides.reduce((sum, s, i) => {
          let isValid = false
          if (s.optionsStats && Array.isArray(s.optionsStats) && s.optionsStats.some(opt => opt.on || opt.isCorrect)) {
            isValid = true
          } else {
            const pageId = s.pageId || s.page || s.id || (this.slides && this.slides.length === this.analysis.slides.length ? (this.slides[i] && (this.slides[i].page || this.slides[i].id)) : null)
            isValid = this.hasValidAnswer(pageId)
          }
          if (isValid) {
            return sum + (s.totalResponses || 0)
          }
          return sum
        }, 0)
        return total - this.correctCount
      }
      return 0
    },
    totalAnswers() {
      return this.correctCount + this.incorrectCount
    },
    overallAccuracy() {
      if (!this.totalAnswers) return 0
      return Math.round((this.correctCount / this.totalAnswers) * 100)
    },
    accuracyColor() {
      if (this.overallAccuracy >= 80) return '#00ffff'
      if (this.overallAccuracy >= 50) return '#ffaa00'
      return '#ff3366'
    },
    countdownPct() {
      return (this.countdown / 10) * 100
    },
  },
  watch: {
    winningTeamFromRemote: {
      handler(team) {
        if (team && this.selectionMode) {
          this.selectedWinner = team
          this.selectionMode = false
          this.startCountdown()
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (!this.selectionMode) {
      this.startCountdown()
    }
  },
  beforeDestroy() {
    if (this.countdownInterval) clearInterval(this.countdownInterval)
  },
  methods: {
    getFullQuestion(slideId) {
      if (!slideId) return null
      const fromSlides = this.slides.find((s) => s.page === slideId || s.id === slideId)
      const fromSession = this.sessionQuestions.find((q) => q.page === slideId || q.id === slideId)
      return fromSession || fromSlides
    },
    hasValidAnswer(slideId) {
      const fullQuestion = this.getFullQuestion(slideId)
      if (!fullQuestion || !fullQuestion.options) return false
      try {
        const opts = typeof fullQuestion.options === 'string' ? JSON.parse(fullQuestion.options) : fullQuestion.options
        if (!Array.isArray(opts)) return false
        return opts.some((opt) => opt.on === true || opt.isCorrect === true)
      } catch (e) {
        return false
      }
    },
    startCountdown() {
      if (this.countdownInterval) clearInterval(this.countdownInterval)
      this.countdownInterval = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.countdownInterval)
          this.$emit('close')
        }
      }, 1000)
    },
    declareWinner(team) {
      this.selectedWinner = team
      this.$emit('declare-winner', team)
      this.selectionMode = false
      this.startCountdown()
    },
    truncateLetters(str, limit) {
      if (!str) return ''
      if (str.length <= limit) return str
      return str.substring(0, limit) + '...'
    },
    getContrastColor(hexcolor) {
      if (!hexcolor) return '#000000'
      const hex = hexcolor.replace('#', '')
      if (hex.length !== 6) return '#000000'
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      const yiq = (r * 299 + g * 587 + b * 114) / 1000
      return yiq >= 128 ? '#000000' : '#ffffff'
    },
    onReturn() {
      clearInterval(this.countdownInterval)
      this.$emit('close')
    },
    getInitials(name) {
      if (!name) return '?'
      const p = name.trim().split(' ')
      return p.length === 1 ? p[0].substring(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase()
    },
    avatarBg(idx) {
      return AV_COLORS[idx % AV_COLORS.length]
    },
    lvlColor(idx) {
      return LVL_COLORS[idx % LVL_COLORS.length]
    },
    lvlBg(idx) {
      return LVL_COLORS[idx % LVL_COLORS.length] + '22'
    },
  },
}
</script>

<style scoped>
/* ── OVERLAY & PAGE ── */
.go-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(8, 18, 18, 0.7);
  backdrop-filter: blur(20px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 300000;
  padding: 24px;
  box-sizing: border-box;
}

/* ── LOADING STATE ── */
.go-loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  text-align: center;
  backdrop-filter: blur(10px);
  border-radius: 12px;
}
.go-loader-icon {
  font-size: 48px;
  color: #00ffff;
  margin-bottom: 24px;
  text-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
}
.go-loading-text {
  font-size: 24px;
  color: #fff;
  letter-spacing: 4px;
  margin: 0 0 12px;
  font-weight: 800;
}
.go-loading-sub {
  color: #7be8e8;
  font-size: 14px;
  opacity: 0.8;
  letter-spacing: 1px;
}

.go-page {
  background: #071214;
  border: 1px solid #1a3c40;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 840px;
  max-height: 100%;
  overflow-y: auto;
  font-family: Arial, sans-serif;
  padding: 0 0 32px;
  box-sizing: border-box;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
}

.go-page > * {
  flex-shrink: 0;
}

/* ── HEADER PILL ── */
.go-header-pill {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  border: 1px solid #2a4a50;
  border-radius: 30px;
  padding: 7px 22px;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  color: #7be8e8;
  background: #0c1e22;
  margin-bottom: 10px;
}

/* ── WINNER SECTION ── */
.go-winner-wrap {
  display: flex;
  align-items: flex-start;
  gap: 60px;
  max-width: 760px;
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
  margin-bottom: 32px;
}

/* Trophy col */
.go-trophy-col {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 200px;
}
.go-trophy-glow-wrap {
  position: relative;
  width: 160px;
  height: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.go-trophy-glow-wrap::before {
  content: '';
  position: absolute;
  width: 130px;
  height: 130px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 255, 255, 0.18) 0%, transparent 70%);
  animation: goGlowPulse 2.2s ease-in-out infinite alternate;
}
@keyframes goGlowPulse {
  from {
    transform: scale(0.85);
    opacity: 0.5;
  }
  to {
    transform: scale(1.15);
    opacity: 1;
  }
}
.go-trophy-icon {
  font-size: 100px;
  color: #00ffff;
  filter: drop-shadow(0 0 18px #00ffff) drop-shadow(0 0 36px rgba(0, 255, 255, 0.5));
  position: relative;
  z-index: 1;
}
.go-winner-badge {
  margin-top: 10px;
  border: 2px solid #00ffff;
  border-radius: 4px;
  padding: 9px 32px;
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 5px;
  color: #00ffff;
  background: rgba(0, 255, 255, 0.05);
  text-align: center;
  width: 180px;
  box-sizing: border-box;
}

/* Sparkles */
.go-sparkle,
.go-confetti {
  position: absolute;
  pointer-events: none;
  user-select: none;
}
.go-sp-tl {
  top: 0px;
  left: 14px;
  color: #7be8e8;
  font-size: 18px;
  opacity: 0.7;
  animation: goTwink 1.6s ease-in-out infinite alternate;
}
.go-sp-tr {
  top: 20px;
  right: 6px;
  color: #7be8e8;
  font-size: 14px;
  opacity: 0.8;
  animation: goTwink 1.2s ease-in-out infinite alternate 0.3s;
}
.go-sp-tr2 {
  top: 6px;
  right: 30px;
  color: #7be8e8;
  font-size: 10px;
  opacity: 0.6;
  animation: goTwink 1.8s ease-in-out infinite alternate 0.6s;
}
.go-confetti {
  bottom: 50px;
  right: -10px;
  font-size: 26px;
  animation: goTwink 2s ease-in-out infinite alternate 0.2s;
}
@keyframes goTwink {
  from {
    opacity: 0.3;
    transform: scale(0.9) rotate(-8deg);
  }
  to {
    opacity: 1;
    transform: scale(1.1) rotate(8deg);
  }
}

/* Winner info */
.go-winner-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 12px;
}
.go-winner-name {
  font-size: 38px;
  font-weight: bold;
  letter-spacing: 3px;
  text-shadow: 0 0 24px currentColor;
  line-height: 1;
  text-transform: uppercase;
}
.go-pts-pill {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  background: #1a3330;
  color: #ffd700;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1px;
  padding: 5px 14px;
  border-radius: 20px;
}

/* Members card */
.go-members-card {
  background: #0c1e22;
  border: 1px solid #1a3a40;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}
.go-mem-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}
.go-mem-avatar {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 15px;
  font-weight: bold;
  color: #00ffff;
  border: 2px solid #1a3c40;
}
.go-mem-name {
  font-size: 9px;
  color: #fff;
  font-weight: bold;
  letter-spacing: 0.5px;
}
.go-mem-lvl {
  font-size: 8px;
  font-weight: bold;
  letter-spacing: 1px;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid;
}

/* ── DIVIDER ── */
.go-divider {
  max-width: 760px;
  width: calc(100% - 40px);
  height: 1px;
  background: linear-gradient(to right, transparent, #2a4a50, transparent);
  margin: 0 0 28px;
}

/* ── LEADERBOARD GRID ── */
.go-lb-grid {
  max-width: 760px;
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 28px;
}
.go-lb-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #0c1a1f;
  border: 1px solid #1a3340;
  border-left: 4px solid #00ffff;
  border-radius: 6px;
  padding: 14px 18px;
}
.go-lb-rank {
  font-size: 22px;
  font-weight: bold;
  min-width: 32px;
}
.go-lb-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.go-lb-name {
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  letter-spacing: 1px;
  text-transform: uppercase;
}
.go-lb-avatars {
  display: flex;
}
.go-mini-av {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 7px;
  font-weight: bold;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: -5px;
  border: 1px solid #071214;
}
.go-mini-av:first-child {
  margin-left: 0;
}
.go-mini-more {
  background: #1a3c40;
  color: #00ffff;
}
.go-lb-score-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.go-lb-pts {
  font-size: 22px;
  font-weight: bold;
}
.go-lb-pts-lbl {
  font-size: 8px;
  color: #8c99a0;
  letter-spacing: 1px;
}

/* ── CLASS PERFORMANCE ── */
.go-perf-wrap {
  max-width: 760px;
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
  margin-bottom: 28px;
}
.go-perf-title {
  font-size: 10px;
  font-weight: bold;
  color: #7a8e96;
  letter-spacing: 2px;
  text-align: center;
  margin-bottom: 0;
}
.go-perf-row {
  background: #0c1a1f;
  border: 1px solid #1a3a40;
  border-radius: 10px;
  padding: 28px 40px;
  display: flex;
  align-items: center;
  gap: 36px;
  justify-content: center;
  margin-top: 14px;
}
.go-acc-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.go-acc-pct {
  font-size: 54px;
  font-weight: bold;
  line-height: 1;
}
.go-acc-lbl {
  font-size: 8px;
  color: #7be8e8;
  letter-spacing: 2px;
  font-weight: bold;
  margin-top: 4px;
}
.go-perf-stats-wrap {
  display: flex;
  align-items: center;
  gap: 36px;
}
.go-stat-complex {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.go-stat-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.go-stat-num {
  font-size: 30px;
  font-weight: bold;
  line-height: 1.1;
}
.go-stat-lbl {
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 1px;
  color: #7a8e96;
}
.go-x-icon {
  font-size: 20px;
  color: #ff3366;
  font-weight: bold;
}

/* ── MOBILE TABS (Desktop Hidden) ── */
.go-mobile-tabs {
  display: none;
}

/* ── RESPONSIVE / MOBILE ── */
@media (max-width: 600px) {
  .go-overlay {
    padding: 0;
  }
  .go-page {
    border: none;
    border-radius: 0;
    max-width: 100%;
    height: 100%;
    box-shadow: none;
    padding: 20px 16px 40px;
  }
  .go-header-pill {
    margin-top: 10px;
    margin-bottom: 24px;
  }
  .go-winner-wrap {
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 0;
    margin-bottom: 24px;
  }
  .go-winner-info {
    width: 100%;
    align-items: center;
    text-align: center;
    padding-top: 0;
  }
  .go-winner-name {
    font-size: 30px;
  }
  .go-members-card {
    justify-content: center;
    width: 100%;
    gap: 16px;
    padding: 16px 12px;
  }
  .go-divider {
    display: none;
  }
  .go-mobile-tabs {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-bottom: 24px;
    border-bottom: 2px solid #1a3c40;
  }
  .go-tab {
    flex: 1;
    text-align: center;
    font-size: 13px;
    font-weight: bold;
    color: #8c99a0;
    padding: 12px 0;
    cursor: pointer;
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
  }
  .go-tab.active {
    color: #00ffff;
    border-bottom-color: #00ffff;
  }
  .go-mobile-hidden {
    display: none !important;
  }
  .go-lb-grid {
    grid-template-columns: 1fr;
    padding: 0;
    width: 100%;
    max-height: 250px;
    overflow-y: auto;
  }
  .go-perf-wrap {
    background: #0c1a1f;
    border: 1px solid #1a3a40;
    border-radius: 10px;
    padding: 20px 16px;
    width: 100%;
    margin-bottom: 24px;
  }
  .go-perf-title {
    margin-bottom: 24px;
  }
  .go-perf-row {
    background: transparent;
    border: none;
    padding: 0;
    margin-top: 0;
    flex-direction: column;
    gap: 24px;
  }
  .go-acc-block {
    align-items: center;
  }
  .go-perf-stats-wrap {
    gap: 16px;
  }
  .go-stat-complex {
    flex-direction: row;
    gap: 8px;
  }
}

/* ── RETURN BUTTON ── */
.go-return-btn {
  background: transparent;
  border: 1px solid #2a4a50;
  color: #8c99a0;
  padding: 14px 48px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 2px;
  cursor: pointer;
  transition: 0.2s;
  margin-bottom: 18px;
}
.go-return-btn:hover {
  border-color: #00ffff;
  color: #00ffff;
}

/* ── COUNTDOWN ── */
.go-countdown-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.go-cd-bar-wrap {
  width: 100px;
  height: 3px;
  background: #1a3c40;
  border-radius: 2px;
  overflow: hidden;
}
.go-cd-bar {
  height: 100%;
  background: #00ffff;
  border-radius: 2px;
  transition: width 1s linear;
}
.go-cd-text {
  font-size: 10px;
  color: #7a8e96;
}

/* scrollbar */
::-webkit-scrollbar {
  width: 5px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #1a3c40;
  border-radius: 3px;
}

/* ── SELECTION PHASE ── */
.go-page-selection {
  max-width: 1000px !important;
  background: rgba(7, 18, 20, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 255, 255, 0.2);
  padding: 40px !important;
}
.go-close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  color: #8c99a0;
  font-size: 24px;
  cursor: pointer;
  transition: 0.2s;
  z-index: 100;
}
.go-close-btn:hover {
  color: #fff;
  transform: rotate(90deg);
}
.go-sel-header {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  margin-top: 0 !important;
}
.go-header-sep {
  margin: 0 10px;
  opacity: 0.3;
}
.go-header-sub {
  color: #00ffff;
  font-size: 10px;
  letter-spacing: 1.5px;
  font-weight: bold;
}
.go-sel-main-title {
  font-size: 28px;
  font-weight: 800;
  color: #00ffff;
  text-align: center;
  margin-bottom: 40px;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  text-transform: uppercase;
}
.go-sel-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
  padding: 0 10px;
  box-sizing: border-box;
}

@media (max-width: 900px) {
  .go-sel-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
.go-sel-card {
  background: #0c1a1f;
  border: 1px solid #1a3340;
  border-left-width: 4px;
  border-left-style: solid;
  border-radius: 8px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  transition: 0.3s;
  position: relative;
  overflow: hidden;
}
.go-sel-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 40px -10px var(--team-glow);
  border-color: var(--team-color);
}
.go-sel-bg-initial {
  position: absolute;
  top: -20px;
  right: -10px;
  font-size: 120px;
  font-weight: 900;
  opacity: 0.03;
  pointer-events: none;
  z-index: 0;
  user-select: none;
}
.go-sel-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
.go-sel-team-name {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  letter-spacing: 1px;
  text-transform: uppercase;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.go-tooltip-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.go-custom-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: 100%;
  left: 0;
  transform: translateY(5px);
  background: rgba(12, 26, 31, 0.95);
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  white-space: nowrap;
  pointer-events: none;
  transition: opacity 0.2s, visibility 0.2s, transform 0.2s;
  z-index: 10000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-transform: none;
  font-weight: bold;
}
.go-tooltip-wrapper:hover .go-custom-tooltip {
  visibility: visible;
  opacity: 1;
  transform: translateY(0);
}
.go-sel-badges {
  display: flex;
  gap: 8px;
  align-items: center;
}
.go-sel-player-count {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 9px;
  font-weight: bold;
}
.go-sel-members {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 30px;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 8px;
}
.go-sel-member {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.03);
  padding: 8px 12px;
  border-radius: 8px;
}
.go-sel-av {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.go-sel-name {
  font-size: 11px;
  color: #e2e8f0;
  font-weight: bold;
  text-transform: uppercase;
}
.go-declare-btn {
  width: 100%;
  border: none;
  padding: 14px;
  border-radius: 8px;
  color: #000;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 1px;
  cursor: pointer;
  transition: 0.2s;
}
.go-declare-btn:hover {
  filter: brightness(1.2);
  transform: scale(1.02);
}

@media (max-width: 600px) {
  .go-page-selection {
    padding: 24px 16px !important;
  }
  .go-sel-main-title {
    font-size: 22px;
    margin-bottom: 24px;
  }
  .go-sel-grid {
    grid-template-columns: 1fr;
  }
}
</style>
